/**
 * Datos del flujo de activación (T033; wiring §4.2 y §8.1). TanStack Query sobre `api/client.ts`.
 *
 * Rutas públicas del realm staff (`config: { public: true }` en la API): no exigen cookie, pero sí
 * `X-Requested-With: fermin-staff` en las mutaciones (lo pone el cliente). `enroll` es la única de
 * este archivo que va firmada **sin** `id=` (`includeDeviceId: false`, wiring §8.1).
 *
 * El token de invitación solo se usa para construir la URL: nunca se loguea ni se renderiza.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  ActivationInfoSchema,
  EnrollResponseSchema,
  HandoffCodeResponseSchema,
  RedeemCodeResponseSchema,
  type ActivationInfo,
  type DeviceEnrollBody,
  type EnrollResponse,
  type HandoffCodeResponse,
  type RedeemCodeResponse,
} from '@fermin/contracts';

import { api, ApiError } from '../../api/client';
import { exportPublicJwk, resetKey } from '../../device/keys';
import { clientHints, detectPlatform, displayMode, userAgent } from '../../device/platform';
import { DEVICE_KEY_QUERY_KEY } from '../access/device-key-query';

export const ACTIVATION_QUERY_KEY = ['activation'] as const;

function activationPath(token: string, suffix = ''): string {
  return `/activation/${encodeURIComponent(token)}${suffix}`;
}

// ---------------------------------------------------------------------------
// GET /activation/{token}
// ---------------------------------------------------------------------------

export function fetchActivationInfo(token: string): Promise<ActivationInfo> {
  return api.get(activationPath(token), { schema: ActivationInfoSchema });
}

export function useActivationInfo(
  token: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<ActivationInfo, ApiError> {
  return useQuery<ActivationInfo, ApiError>({
    queryKey: [...ACTIVATION_QUERY_KEY, 'info', token],
    queryFn: () => fetchActivationInfo(token),
    enabled: options.enabled ?? true,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// POST /activation/{token}/verify-document (005 · QA-6)
// ---------------------------------------------------------------------------

/**
 * Últimos 4 del documento. `200` trae el `ActivationInfo` **con** el nombre de pila (el `GET` nunca
 * lo trae). `422 DOCUMENT_MISMATCH` lleva `details.attempts_remaining` (0 = enlace bloqueado).
 * Los dígitos solo viajan en el cuerpo: nunca se guardan ni se loguean.
 */
export function verifyDocument(token: string, documentLast4: string): Promise<ActivationInfo> {
  return api.post(activationPath(token, '/verify-document'), {
    body: { document_last4: documentLast4 },
    schema: ActivationInfoSchema,
  });
}

export function useVerifyDocument(
  token: string,
): UseMutationResult<ActivationInfo, ApiError, string> {
  return useMutation<ActivationInfo, ApiError, string>({
    mutationFn: (documentLast4) => verifyDocument(token, documentLast4),
  });
}

/** `details.attempts_remaining` de un `422 DOCUMENT_MISMATCH`; `null` si el error es otro. */
export function attemptsRemaining(error: ApiError | null): number | null {
  if (!error?.is('DOCUMENT_MISMATCH')) {
    return null;
  }
  const value = error.details?.attempts_remaining;
  return typeof value === 'number' ? value : null;
}

// ---------------------------------------------------------------------------
// POST /activation/{token}/privacy
// ---------------------------------------------------------------------------

export function acceptActivationPrivacy(token: string, version: string): Promise<undefined> {
  return api.post(activationPath(token, '/privacy'), { body: { version } });
}

export function useAcceptActivationPrivacy(
  token: string,
): UseMutationResult<undefined, ApiError, string> {
  return useMutation<undefined, ApiError, string>({
    mutationFn: (version) => acceptActivationPrivacy(token, version),
  });
}

// ---------------------------------------------------------------------------
// POST /activation/{token}/handoff-code (código de traspaso iOS, research R-5)
// ---------------------------------------------------------------------------

export function createHandoffCode(token: string): Promise<HandoffCodeResponse> {
  return api.post(activationPath(token, '/handoff-code'), { schema: HandoffCodeResponseSchema });
}

/**
 * El código se pide al entrar a `/instalar` y se muestra hasta que vence, por eso es una query
 * (aunque el método sea POST: el servidor lo emite, no lo consulta). `staleTime: Infinity` evita
 * generar un código nuevo en cada re-render.
 */
export function useHandoffCode(
  token: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<HandoffCodeResponse, ApiError> {
  return useQuery<HandoffCodeResponse, ApiError>({
    queryKey: [...ACTIVATION_QUERY_KEY, 'handoff', token],
    queryFn: () => createHandoffCode(token),
    enabled: options.enabled ?? true,
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
  });
}

// ---------------------------------------------------------------------------
// POST /activation/redeem-code (PWA instalada: "Tengo un código")
// ---------------------------------------------------------------------------

export function redeemHandoffCode(code: string): Promise<RedeemCodeResponse> {
  return api.post('/activation/redeem-code', {
    body: { code },
    schema: RedeemCodeResponseSchema,
  });
}

export function useRedeemHandoffCode(): UseMutationResult<RedeemCodeResponse, ApiError, string> {
  return useMutation<RedeemCodeResponse, ApiError, string>({
    mutationFn: (code) => redeemHandoffCode(code),
  });
}

// ---------------------------------------------------------------------------
// POST /activation/{token}/enroll
// ---------------------------------------------------------------------------

/**
 * Cuerpo de `DeviceEnrollBodySchema`: crea la clave del dispositivo si aún no existe.
 *
 * 005 · QA-5: añade `client_hints` (modelo y versión del sistema) si el navegador los da; se piden
 * en paralelo con la clave y con plazo corto (`CLIENT_HINTS_TIMEOUT_MS`), así que nunca retrasan
 * ni rompen el registro.
 */
export async function buildEnrollBody(): Promise<DeviceEnrollBody> {
  const [publicKeyJwk, hints] = await Promise.all([exportPublicJwk(), clientHints()]);
  return {
    public_key_jwk: publicKeyJwk,
    platform: detectPlatform(),
    display_mode: displayMode(),
    user_agent: userAgent(),
    ...(hints ? { client_hints: hints } : {}),
  };
}

function postEnroll(token: string, body: DeviceEnrollBody): Promise<EnrollResponse> {
  return api.post(activationPath(token, '/enroll'), {
    body,
    schema: EnrollResponseSchema,
    // Única ruta (con `claim`) que firma sin `id=`: el servidor deriva el device_id de la clave.
    includeDeviceId: false,
  });
}

/**
 * Registra este dispositivo. Si la API responde `409 CONFLICT` la clave de este navegador ya está
 * registrada para otro trabajador (o quedó huérfana tras una revocación): se descarta con
 * `resetKey()` y se reintenta **una sola vez** con una clave nueva (wiring §8.1).
 */
export async function enrollDevice(token: string): Promise<EnrollResponse> {
  try {
    return await postEnroll(token, await buildEnrollBody());
  } catch (error) {
    if (error instanceof ApiError && error.is('CONFLICT')) {
      await resetKey();
      return await postEnroll(token, await buildEnrollBody());
    }
    throw error;
  }
}

/**
 * `DEVICE_KEY_QUERY_KEY` se deja en `true` a mano: `HomeGate` la cachea con
 * `staleTime: Number.POSITIVE_INFINITY` y, si el trabajador abrió `/` antes de seguir el enlace de
 * activación, seguiría leyendo `false` y rebotaría a `/dispositivo-no-registrado` ya enrolado.
 */
export function useEnrollDevice(token: string): UseMutationResult<EnrollResponse, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<EnrollResponse, ApiError, void>({
    mutationFn: () => enrollDevice(token),
    onSuccess: () => {
      queryClient.setQueryData(DEVICE_KEY_QUERY_KEY, true);
    },
  });
}
